/**
 * ELTAHAN PDP sale price v3.4 — Impact theme variant synchronization.
 * The snippet embeds a preformatted price map for every variant.
 * This script ONLY updates the visible UI. It never changes Shopify cart prices.
 */
(() => {
  'use strict';

  if (window.__eltahanPdpSalePriceV34) return;
  window.__eltahanPdpSalePriceV34 = true;

  const SELECTOR = '.eltahan-pdp-price[data-eltahan-product-id]';
  const cache = new WeakMap();
  const recentlyChanged = new Map();
  let initQueued = false;

  function widgets() {
    return Array.from(document.querySelectorAll(SELECTOR));
  }

  function pricesFor(widget) {
    if (cache.has(widget)) return cache.get(widget);
    const data = widget.querySelector('script.eltahan-pdp-price__variants');
    if (!data) return null;
    try {
      const prices = JSON.parse(data.textContent);
      cache.set(widget, prices);
      return prices;
    } catch (error) {
      console.warn('[ELTAHAN price v3.4] Could not read variant prices', error);
      return null;
    }
  }

  function setVariant(widget, variantId) {
    if (!widget || variantId == null) return false;
    const price = pricesFor(widget)?.[String(variantId)];
    if (!price) return false;

    const current = widget.querySelector('[data-eltahan-current]');
    const compare = widget.querySelector('[data-eltahan-compare]');
    if (!current || !compare) return false;

    if (current.textContent !== price.current) current.textContent = price.current;
    if (compare.textContent !== price.compare) compare.textContent = price.compare;
    widget.dataset.eltahanActiveVariant = String(variantId);
    return true;
  }

  function readVariantFromForm(widget) {
    const formId = widget.dataset.eltahanFormId;
    if (!formId) return null;
    const form = document.getElementById(formId);
    if (!form) return null;
    const input = form.querySelector('input[name="id"], select[name="id"]');
    return input?.value || null;
  }

  function readVariantFromPageUrl(widget) {
    // Only the main PDP should read the URL; other featured products must not.
    if (!widget.closest('.shopify-section--main-product')) return null;
    return new URLSearchParams(location.search).get('variant');
  }

  function syncWidget(widget, preferUrl = false) {
    const variantId = (preferUrl ? readVariantFromPageUrl(widget) : null)
      || readVariantFromForm(widget)
      || readVariantFromPageUrl(widget)
      || widget.dataset.eltahanInitialVariant;
    setVariant(widget, variantId);
  }

  function syncAll(preferUrl = false) {
    widgets().forEach(widget => syncWidget(widget, preferUrl));
  }

  function repaintEvent(formId, variantId, target, productId) {
    const all = widgets();
    let matches = formId
      ? all.filter(widget => widget.dataset.eltahanFormId === formId)
      : [];

    // Fallback if an app/custom variant picker emitted the event on a
    // wrapper instead of the named form.
    if (!matches.length && target?.closest) {
      const section = target.closest('.shopify-section, .quick-buy-drawer');
      if (section) {
        matches = all.filter(widget => section.contains(widget)
          && (!productId || widget.dataset.eltahanProductId === String(productId)));
      }
    }

    matches.forEach(widget => setVariant(widget, variantId));
  }

  // Official Maestrooo Impact/Prestige v7+ event, dispatched on the form.
  document.addEventListener('variant:change', event => {
    const variantId = event.detail?.variant?.id;
    if (variantId == null) return;
    // A product form contains input[name=id], which can shadow form.id.
    const formId = event.target?.getAttribute?.('id') || event.detail?.formId || '';
    const productId = event.detail?.product?.id || null;
    if (formId) recentlyChanged.set(formId, performance.now());

    repaintEvent(formId, variantId, event.target, productId);
    // Some versions update the surrounding DOM immediately after this event.
    queueMicrotask(() => repaintEvent(formId, variantId, event.target, productId));
    requestAnimationFrame(() => repaintEvent(formId, variantId, event.target, productId));
  });

  // Fallback for custom pickers / apps that don't emit variant:change.
  // Wait for the theme to update its hidden [name=id] product form input.
  function maybeSyncFromForm(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches('[name="id"]') && !target.closest(
      'variant-picker, .variant-picker, .product-info__variant-picker, .quick-buy-drawer'
    )) return;

    const closestForm = target.closest('form');
    const picker = target.closest('variant-picker');
    const scope = target.closest('.product-info, .quick-buy-drawer, .shopify-section');
    const associatedWidget = scope?.querySelector(SELECTOR);
    // Form IDs must be read from attributes; form.id may be shadowed by
    // Shopify's input[name="id"].
    const formId = closestForm?.getAttribute('id')
      || picker?.getAttribute('form')
      || target.getAttribute('form')
      || associatedWidget?.dataset.eltahanFormId
      || '';
    const refresh = () => {
      if (formId && performance.now() - (recentlyChanged.get(formId) || -10000) < 400) return;
      if (formId) {
        widgets().filter(widget => widget.dataset.eltahanFormId === formId)
          .forEach(widget => syncWidget(widget));
      } else if (scope) {
        scope.querySelectorAll(SELECTOR).forEach(widget => syncWidget(widget));
      }
    };
    requestAnimationFrame(refresh);
    setTimeout(refresh, 120);
  }
  document.addEventListener('change', maybeSyncFromForm, true);
  document.addEventListener('input', maybeSyncFromForm, true);

  // Back/forward between ?variant= URLs.
  window.addEventListener('popstate', () => syncAll(true));
  document.addEventListener('shopify:section:load', () => syncAll());

  function queueInit() {
    if (initQueued) return;
    initQueued = true;
    requestAnimationFrame(() => {
      initQueued = false;
      // Don't override an authoritative variant:change that just happened.
      widgets().forEach(widget => {
        if (widget.dataset.eltahanActiveVariant) return;
        syncWidget(widget);
      });
    });
  }

  // Quick Buy draws its HTML after initial page load. Initialize only when
  // new price widgets are added; ignore unrelated DOM/text changes.
  function watchNewWidgets() {
    if (!document.body) return;
    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR)) {
            queueInit();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    syncAll();
    watchNewWidgets();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
